import Autocomplete from "@mui/material/Autocomplete";
import Chip from "@mui/material/Chip";
import type { ChipProps } from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useQuery } from "@tanstack/react-query";
import {
  Briefcase,
  Building,
  Cpu,
  Factory,
  Flame,
  FlaskConical,
  GraduationCap,
  HardHat,
  Heart,
  Home,
  Landmark,
  Leaf,
  Monitor,
  Music,
  Package,
  Printer,
  Recycle,
  Server,
  Shield,
  ShoppingCart,
  Stethoscope,
  Tag,
  TreePine,
  Truck,
  Utensils,
  Wifi,
  Wrench,
  Zap,
} from "lucide-mui";
import { useMemo, useState } from "react";

import { keys } from "../api/queryKeys.ts";
import { api, type CpvCodeItem } from "../api/client.ts";
import { useDebouncedValue } from "../hooks/useDebouncedValue.ts";

import type { SvgIconProps } from "@mui/material/SvgIcon";

type LucideIcon = React.ComponentType<SvgIconProps>;

const CPV_DIVISION_ICONS: Array<[string, LucideIcon]> = [
  ["03", Leaf],       // Agriculture, forestry
  ["09", Flame],      // Petroleum, fuel, energy
  ["14", Factory],    // Mining, basic metals
  ["15", Utensils],   // Food, beverages, tobacco
  ["16", Wrench],     // Agricultural machinery
  ["18", ShoppingCart], // Clothing, footwear
  ["22", Printer],    // Printed matter
  ["24", FlaskConical], // Chemical products
  ["30", Cpu],        // Office and computing machinery
  ["31", Zap],        // Electrical machinery
  ["32", Wifi],       // Radio, telecom equipment
  ["33", Stethoscope], // Medical equipment
  ["34", Truck],      // Transport equipment
  ["35", Shield],     // Security, defence equipment
  ["42", Factory],    // Industrial machinery
  ["43", Factory],    // Mining/construction machinery
  ["44", Building],   // Construction materials
  ["45", HardHat],    // Construction work
  ["48", Monitor],    // Software packages
  ["50", Wrench],     // Repair and maintenance
  ["55", Utensils],   // Hotel, restaurant services
  ["60", Truck],      // Transport services
  ["64", Wifi],       // Postal and telecom services
  ["65", Zap],        // Public utilities
  ["66", Landmark],   // Financial and insurance services
  ["70", Home],       // Real estate services
  ["71", Building],   // Architectural, engineering services
  ["72", Server],     // IT services
  ["73", FlaskConical], // R&D services
  ["75", Landmark],   // Public administration
  ["77", TreePine],   // Agricultural services
  ["79", Briefcase],  // Business services
  ["80", GraduationCap], // Education and training
  ["85", Heart],      // Health and social work
  ["90", Recycle],    // Environmental services
  ["92", Music],      // Cultural and sporting services
];

const CPV_ICON_MAP = new Map<string, LucideIcon>(CPV_DIVISION_ICONS);

function getCpvIcon(code: string): LucideIcon {
  if (!/^\d{8}$/.test(code)) return Tag;
  const division = code.slice(0, 2);
  return CPV_ICON_MAP.get(division) ?? Package;
}

type DescriptionMode = "tooltip" | "inline" | "none";

const DEFAULT_LANG = "en";

interface CpvCodeListProps {
  codes: string[] | null | undefined;
  lang?: string;
  showDescription?: DescriptionMode;
  variant?: ChipProps["variant"];
  size?: ChipProps["size"];
  emptyFallback?: React.ReactNode;
}

interface CpvCodeAutocompleteProps {
  value: string | null;
  onChange: (code: string | null) => void;
  lang?: string;
  label?: string;
  size?: "small" | "medium";
  placeholder?: string;
  autoFocus?: boolean;
}

export interface CpvLookupResult {
  description: string;
  matchedVia: string;
}

/**
 * Walk a code up the CPV hierarchy by zeroing trailing digits. CPV is hierarchical:
 * `72211000` → `72210000` → `72200000` → `72000000` → `70000000`.
 */
function* walkUp(code: string): Generator<string> {
  if (!/^\d{8}$/.test(code)) return;
  let current = code;
  yield current;
  while (true) {
    const i = current.search(/0*$/);
    if (i <= 0) return;
    current = current.slice(0, i - 1) + "0" + current.slice(i);
    yield current;
  }
}

export function lookupCpv(
  code: string,
  map: Map<string, string>,
): CpvLookupResult | undefined {
  for (const candidate of walkUp(code)) {
    const description = map.get(candidate);
    if (description !== undefined) {
      return { description, matchedVia: candidate };
    }
  }
  return undefined;
}

export function useCpvDescriptions(
  codes: readonly string[],
  lang: string = DEFAULT_LANG,
): { map: Map<string, string>; isLoading: boolean } {
  const key = useMemo(() => {
    const dedup = Array.from(new Set(codes.filter((c) => c && c.length > 0)));
    dedup.sort();
    return dedup;
  }, [codes]);

  const query = useQuery({
    queryKey: keys.cpv.lookup(lang, key),
    queryFn: () => api.getCpvLookup(key, lang),
    enabled: key.length > 0,
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: Number.POSITIVE_INFINITY,
  });

  const map = useMemo(() => {
    const m = new Map<string, string>();
    for (const item of query.data?.items ?? []) {
      m.set(item.code, item.description);
    }
    return m;
  }, [query.data]);

  return { map, isLoading: query.isLoading };
}

function describeForTooltip(
  code: string,
  result: CpvLookupResult | undefined,
): string {
  if (!result) return code;
  if (result.matchedVia === code) return `${code} — ${result.description}`;
  return `${code} — under ${result.matchedVia}: ${result.description}`;
}

export default function CpvCodeList({
  codes,
  lang = DEFAULT_LANG,
  showDescription = "inline",
  variant = "outlined",
  size = "small",
  emptyFallback = null,
}: CpvCodeListProps) {
  const list = useMemo(() => codes ?? [], [codes]);
  const { map } = useCpvDescriptions(list, lang);

  if (list.length === 0) return <>{emptyFallback}</>;

  return (
    <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: "wrap" }}>
      {list.map((c) => {
        const result = lookupCpv(c, map);
        const description = result?.description;
        const Icon = getCpvIcon(c);
        const icon = <Icon fontSize="small" />;
        if (showDescription === "none") {
          return <Chip key={c} size={size} label={c} icon={icon} variant={variant} />;
        }
        if (showDescription === "inline") {
          const label = description ? `${c} — ${description}` : c;
          return <Chip key={c} size={size} label={label} icon={icon} variant={variant} />;
        }
        const title = description ? describeForTooltip(c, result) : c;
        return (
          <Tooltip key={c} title={title}>
            <Chip key={c} size={size} label={c} icon={icon} variant={variant} />
          </Tooltip>
        );
      })}
    </Stack>
  );
}

export function CpvCodeAutocomplete({
  value,
  onChange,
  lang = DEFAULT_LANG,
  label = "CPV code",
  size = "small",
  placeholder,
  autoFocus,
}: CpvCodeAutocompleteProps) {
  const [inputValue, setInputValue] = useState("");
  const debounced = useDebouncedValue(inputValue, 250);

  const search = useQuery({
    queryKey: keys.cpv.search(lang, debounced),
    queryFn: () => api.searchCpv(debounced, lang, 20),
    enabled: debounced.trim().length > 0,
    staleTime: 5 * 60 * 1000,
  });

  // Resolve the description for the current selected value (if any) so the
  // selected chip can show its label.
  const selectedLookup = useCpvDescriptions(value ? [value] : [], lang);
  const selectedDescription = value ? (selectedLookup.map.get(value) ?? "") : "";
  const selectedOption: CpvCodeItem | null = useMemo(
    () => (value ? { code: value, lang, description: selectedDescription } : null),
    [value, lang, selectedDescription],
  );

  const options: CpvCodeItem[] = useMemo(() => {
    const items = search.data?.items ?? [];
    if (selectedOption && !items.some((i) => i.code === selectedOption.code)) {
      return [selectedOption, ...items];
    }
    return items;
  }, [search.data, selectedOption]);

  return (
    <Autocomplete
      size={size}
      options={options}
      value={selectedOption}
      onChange={(_, option) => onChange(option?.code ?? null)}
      inputValue={inputValue}
      onInputChange={(_, v) => setInputValue(v)}
      loading={search.isFetching}
      filterOptions={(x) => x}
      getOptionLabel={(option) =>
        option.description ? `${option.code} — ${option.description}` : option.code
      }
      isOptionEqualToValue={(option, val) => option.code === val.code}
      renderOption={(props, option) => {
        const { key, ...rest } = props;
        return (
          <li key={key} {...rest}>
            <Stack>
              <Typography variant="body2" component="span">
                {option.code}
              </Typography>
              {option.description && (
                <Typography variant="caption" color="text.secondary" component="span">
                  {option.description}
                </Typography>
              )}
            </Stack>
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField {...params} label={label} placeholder={placeholder} autoFocus={autoFocus} />
      )}
    />
  );
}
